import { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
} from "recharts";
import {
  Users,
  Building,
  Calendar,
  Activity,
  TrendingUp,
  Clock,
  Shield,
  AlertTriangle,
} from "lucide-react";
import { secureRandom } from "../../components/streaming/utils/secureRandom";

// ---------- Types ----------
interface RealtimeData {
  time: string;
  users: number;
  sessions: number;
}

interface CourtData {
  name: string;
  rooms: number;
  active: number;
}

interface SessionTypeData {
  name: string;
  value: number;
  color: string;
  [key: string]: string | number;
}

interface WeeklyData {
  day: string;
  courts: number;
  sessions: number;
  users: number;
}

interface StatCardProps {
  title: string;
  value: number;
  icon: any;
  color: string;
  subtitle?: string;
  trend?: string;
}

// ---------- Component ----------
const SuperAdminDashboard = () => {
  const [_, setCurrentTime] = useState<Date>(new Date());
  const [activeUsers, setActiveUsers] = useState<number>(1247);
  const [currentSessions, setCurrentSessions] = useState<number>(89);
  const [courts] = useState<number>(24);
  const [courtRooms] = useState<number>(156);

  const [realtimeData, setRealtimeData] = useState<RealtimeData[]>([
    { time: "09:00", users: 890, sessions: 45 },
    { time: "10:00", users: 1020, sessions: 62 },
    { time: "11:00", users: 1150, sessions: 78 },
    { time: "12:00", users: 1200, sessions: 85 },
    { time: "13:00", users: 1247, sessions: 89 },
  ]);

  const courtData: CourtData[] = [
    { name: "Supreme Court", rooms: 12, active: 8 },
    { name: "High Court", rooms: 28, active: 22 },
    { name: "District Court", rooms: 45, active: 38 },
    { name: "Family Court", rooms: 32, active: 25 },
    { name: "Civil Court", rooms: 39, active: 31 },
  ];

  const sessionTypeData: SessionTypeData[] = [
    { name: "Civil Cases", value: 35, color: "#8b5cf6" },
    { name: "Criminal Cases", value: 28, color: "#06b6d4" },
    { name: "Family Cases", value: 15, color: "#10b981" },
    { name: "Appeals", value: 11, color: "#f59e0b" },
  ];

  const weeklyData: WeeklyData[] = [
    { day: "Mon", courts: 22, sessions: 78, users: 1100 },
    { day: "Tue", courts: 24, sessions: 85, users: 1200 },
    { day: "Wed", courts: 23, sessions: 82, users: 1150 },
    { day: "Thu", courts: 24, sessions: 89, users: 1247 },
    { day: "Fri", courts: 24, sessions: 92, users: 1320 },
    { day: "Sat", courts: 18, sessions: 45, users: 680 },
    { day: "Sun", courts: 12, sessions: 28, users: 420 },
  ];

  // Simulate real-time updates
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());

      setActiveUsers((prev) => prev + Math.floor(secureRandom() * 20) - 10);  
      setCurrentSessions((prev) =>
        Math.max(0, prev + Math.floor(secureRandom() * 6) - 3) 
      );

      setRealtimeData((prev) => {
        const newTime = new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        });
        const newData: RealtimeData = {
          time: newTime,
          users: activeUsers + Math.floor(secureRandom() * 50) - 25, 
          sessions: currentSessions + Math.floor(secureRandom() * 10) - 5, 
        };
        return [...prev.slice(1), newData];
      });
    }, 3000);

    return () => clearInterval(interval);
  }, [activeUsers, currentSessions]);

  const StatCard = ({
    title,
    value,
    icon: Icon,
    color,
    subtitle,
    trend,
  }: StatCardProps) => (
    <div
      className="bg-white rounded-xl shadow-md p-6 border-l-4"
      style={{ borderLeftColor: color }}
    >
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <p className="text-sm font-medium text-gray-600">
            {title}
          </p>

          <p className="text-2xl font-bold text-gray-900 leading-tight">
            {value.toLocaleString()}
          </p>

          {subtitle && (
            <p className="text-xs text-gray-500">
              {subtitle}
            </p>
          )}

          {trend && (
            <div className="flex items-center gap-1 pt-1">
              <TrendingUp className="w-3.5 h-3.5 text-green-500" />
              <span className="text-xs font-medium text-green-600">
                {trend}
              </span>
            </div>
          )}
        </div>

        <div
          className="p-2.5 rounded-lg"
          style={{ backgroundColor: color + "15" }}
        >
          <Icon className="w-6 h-6" style={{ color }} />
        </div>
      </div>
    </div>
  );

  return (
    <div className="max-w-screen w-full min-h-screen">
          <div className="w-full max-w-full lg:p-4 md:p-4 p-4">
        {/* Header */}
        {/* <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900">
              Super Admin Dashboard
            </h1>
            <p className="text-gray-600 mt-2">
              Court Management System Overview
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500">Last Updated</p>
            <p className="text-lg font-semibold text-gray-900">
              {currentTime.toLocaleString()}
            </p>
          </div>
        </div> */}

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <StatCard
            title="Total Courts"
            value={courts}
            icon={Building}
            color="#8b5cf6"
            subtitle="Across all jurisdictions"
            trend="+2 this month"
          />
          <StatCard
            title="Court Rooms"
            value={courtRooms}
            icon={Calendar}
            color="#06b6d4"
            subtitle="Available facilities"
            trend="+5 this week"
          />
          <StatCard
            title="Active Sessions"
            value={currentSessions}
            icon={Activity}
            color="#10b981"
            subtitle="Currently in progress"
          />
          <StatCard
            title="Active Users"
            value={activeUsers}
            icon={Users}
            color="#f59e0b"
            subtitle="Online now"
          />
        </div>

        {/* Real-time Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Real-time User Activity */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">
                Real-time Activity
              </h3>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-sm text-gray-600">Live</span>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={realtimeData}>
                <defs>
                  <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient
                    id="colorSessions"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#06b6d4" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="time" />
                <YAxis />
                <CartesianGrid strokeDasharray="3 3" />
                <Tooltip />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="users"
                  stroke="#8b5cf6"
                  fillOpacity={1}
                  fill="url(#colorUsers)"
                  name="Active Users"
                />
                <Area
                  type="monotone"
                  dataKey="sessions"
                  stroke="#06b6d4"
                  fillOpacity={1}
                  fill="url(#colorSessions)"
                  name="Active Sessions"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Session Types Distribution */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-6">
              Current Session Types
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={sessionTypeData as any}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={120}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {sessionTypeData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Court Performance Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Court Utilization */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-6">
              Court Room Utilization
            </h3>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={courtData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="name"
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="rooms" fill="#e5e7eb" name="Total Rooms" />
                <Bar dataKey="active" fill="#10b981" name="Active Rooms" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Weekly Trends */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-6">
              Weekly Trends
            </h3>
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={weeklyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="courts"
                  stroke="#8b5cf6"
                  strokeWidth={3}
                  name="Active Courts"
                />
                <Line
                  type="monotone"
                  dataKey="sessions"
                  stroke="#06b6d4"
                  strokeWidth={3}
                  name="Sessions"
                />
                <Line
                  type="monotone"
                  dataKey="users"
                  stroke="#f59e0b"
                  strokeWidth={3}
                  name="Users"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Additional Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center space-x-3">
              <Clock className="w-8 h-8 text-blue-500" />
              <div>
                <p className="text-gray-500 text-sm">Avg Session Duration</p>
                <p className="text-2xl font-bold text-gray-900">2.4 hrs</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center space-x-3">
              <Shield className="w-8 h-8 text-green-500" />
              <div>
                <p className="text-gray-500 text-sm">System Uptime</p>
                <p className="text-2xl font-bold text-gray-900">99.8%</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center space-x-3">
              <TrendingUp className="w-8 h-8 text-purple-500" />
              <div>
                <p className="text-gray-500 text-sm">Daily Growth</p>
                <p className="text-2xl font-bold text-gray-900">+12%</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center space-x-3">
              <AlertTriangle className="w-8 h-8 text-red-500" />
              <div>
                <p className="text-gray-500 text-sm">Pending Issues</p>
                <p className="text-2xl font-bold text-gray-900">3</p>
              </div>
            </div>
          </div>
        </div>

        {/* System Status Table */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <h3 className="text-xl font-bold text-gray-900 mb-6">
            Court System Status
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Court Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Rooms
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Active Rooms
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Utilization
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {courtData.map((court, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {court.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {court.rooms}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {court.active}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {Math.round((court.active / court.rooms) * 100)}%
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${court.active / court.rooms > 0.8
                            ? "bg-green-100 text-green-800"
                            : court.active / court.rooms > 0.5
                              ? "bg-yellow-100 text-yellow-800"
                              : "bg-red-100 text-red-800"
                          }`}
                      >
                        {court.active / court.rooms > 0.8
                          ? "High"
                          : court.active / court.rooms > 0.5
                            ? "Medium"
                            : "Low"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Live Activity Feed */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-6">
              Live Activity Feed
            </h3>
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {[
                {
                  time: "14:32",
                  action: "New session started",
                  court: "District Court Room 12",
                  type: "success",
                },
                {
                  time: "14:31",
                  action: "User logged in",
                  user: "Judge Martinez",
                  type: "info",
                },
                {
                  time: "14:29",
                  action: "Session completed",
                  court: "Family Court Room 8",
                  type: "success",
                },
                {
                  time: "14:28",
                  action: "System maintenance alert",
                  message: "Scheduled for 02:00 AM",
                  type: "warning",
                },
                {
                  time: "14:26",
                  action: "New case filed",
                  case: "CV-2024-1247",
                  type: "info",
                },
                {
                  time: "14:25",
                  action: "User logged out",
                  user: "Attorney Johnson",
                  type: "info",
                },
                {
                  time: "14:23",
                  action: "Session started",
                  court: "High Court Room 3",
                  type: "success",
                },
              ].map((activity, index) => (
                <div
                  key={index}
                  className="flex items-center space-x-4 p-3 bg-gray-50 rounded-lg"
                >
                  <span className="text-sm text-gray-500 min-w-16">
                    {activity.time}
                  </span>
                  <div
                    className={`w-2 h-2 rounded-full ${activity.type === "success"
                        ? "bg-green-500"
                        : activity.type === "warning"
                          ? "bg-yellow-500"
                          : "bg-blue-500"
                      }`}
                  ></div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-900">{activity.action}</p>
                    {activity.court && (
                      <p className="text-xs text-gray-600">{activity.court}</p>
                    )}
                    {activity.user && (
                      <p className="text-xs text-gray-600">{activity.user}</p>
                    )}
                    {activity.case && (
                      <p className="text-xs text-gray-600">
                        Case: {activity.case}
                      </p>
                    )}
                    {activity.message && (
                      <p className="text-xs text-gray-600">
                        {activity.message}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-6">
              Quick Actions
            </h3>
            <div className="space-y-4">
              <button className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3 px-4 rounded-lg transition-colors duration-200 font-medium">
                Generate Report
              </button>
              <button className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg transition-colors duration-200 font-medium">
                Manage Courts
              </button>
              <button className="w-full bg-green-600 hover:bg-green-700 text-white py-3 px-4 rounded-lg transition-colors duration-200 font-medium">
                User Management
              </button>
              <button className="w-full bg-yellow-600 hover:bg-yellow-700 text-white py-3 px-4 rounded-lg transition-colors duration-200 font-medium">
                System Settings
              </button>
              <button className="w-full bg-red-600 hover:bg-red-700 text-white py-3 px-4 rounded-lg transition-colors duration-200 font-medium">
                Emergency Actions
              </button>
            </div>

            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">
                System Health
              </h4>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>CPU Usage</span>
                  <span className="text-green-600">42%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-green-500 h-2 rounded-full"
                    style={{ width: "42%" }}
                  ></div>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Memory</span>
                  <span className="text-yellow-600">68%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-yellow-500 h-2 rounded-full"
                    style={{ width: "68%" }}
                  ></div>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Database</span>
                  <span className="text-green-600">89%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-green-500 h-2 rounded-full"
                    style={{ width: "89%" }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center text-gray-500 text-sm mt-6">
          Super Admin Dashboard v2.1.0 • Court Management System • All data
          refreshes every 3 seconds
        </div>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
